import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../../styles/product/ProductCard.css";
import "../../styles/shared/LoadingState.css";
import { useCart } from "../../contexts/CartContext";
import { useWishlist } from "../../contexts/WishlistContext";
import { Product } from "../../types/store";
import { formatCurrency } from "../../utils/currency";
import { resolveProductImageUrl } from "../../utils/productImageUrl";
import { toast } from "../../utils/toast";

interface ProductCardProps {
  product: Product;
  compact?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, compact = false }) => {
  const { addToCart, items, removeFromCart, updateQuantity } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const [hoverDirection, setHoverDirection] = useState<"left" | "right" | null>(
    null
  );

  const previewImages = useMemo(() => {
    const fallbackImage = product.images[0];
    return {
      left: product.images[1] || product.images[0] || fallbackImage,
      right: product.images[2] || product.images[1] || product.images[0] || fallbackImage,
    };
  }, [product.images]);

  const handleMouseMove = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const pointerX = event.clientX - bounds.left;

    if (pointerX < bounds.width / 3) {
      setHoverDirection("left");
      return;
    }

    if (pointerX > (bounds.width * 2) / 3) {
      setHoverDirection("right");
      return;
    }

    setHoverDirection(null);
  };

  const savedToWishlist = isInWishlist(product.id);
  const cartItem = items.find((item) => item.product.id === product.id);
  const cartQuantity = cartItem?.quantity ?? 0;
  const maxQuantity = Math.max(0, product.stockQuantity);

  const handleAddToCart = async () => {
    if (maxQuantity <= 0) {
      toast.error("This item is out of stock.");
      return;
    }

    await addToCart(product);
  };

  const handleQuantityChange = async (quantity: number) => {
    if (maxQuantity <= 0) {
      toast.error("This item is out of stock.");
      return;
    }

    if (quantity <= 0) {
      await removeFromCart(product.id);
      return;
    }

    if (quantity > maxQuantity) {
      toast.error(`Stock not available. Only ${maxQuantity} item${maxQuantity === 1 ? "" : "s"} are in stock.`);
      return;
    }

    const nextQuantity = Math.min(quantity, maxQuantity);
    if (nextQuantity === cartQuantity) {
      return;
    }

    await updateQuantity(product.id, nextQuantity);
  };

  const handleToggleWishlist = async () => {
    await toggleWishlist(product);
  };

  return (
    <article className={`store-card product-card ${compact ? "product-card--compact" : ""}`}>
      <div
        className="product-card__media"
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoverDirection(null)}
      >
        <Link className="product-card__media-link" to={`/products/${product.slug}`}>
          <div className="product-card__image-stage">
            <img
              className={`product-card__image product-card__image--base ${
                hoverDirection === "left"
                  ? "is-leaving-right"
                  : hoverDirection === "right"
                  ? "is-leaving-left"
                  : ""
              }`}
              src={resolveProductImageUrl(product.images[0])}
              alt={product.name}
            />
            <img
              className={`product-card__image product-card__image--left ${
                hoverDirection === "left" ? "is-active" : ""
              }`}
              src={resolveProductImageUrl(previewImages.left)}
              alt={`${product.name} alternate left view`}
            />
            <img
              className={`product-card__image product-card__image--right ${
                hoverDirection === "right" ? "is-active" : ""
              }`}
              src={resolveProductImageUrl(previewImages.right)}
              alt={`${product.name} alternate right view`}
            />
          </div>
          <div className="product-card__hover-zones" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <span className="product-card__discount-badge">
            {product.discountPercentage}% OFF
          </span>
        </Link>

        <div className="product-card__media-actions">
          {cartQuantity > 0 ? (
            <div className="product-card__quantity-control" aria-label={`${product.name} cart quantity`}>
              <button
                type="button"
                onClick={() => void handleQuantityChange(cartQuantity - 1)}
                aria-label={`Reduce ${product.name} quantity`}
              >
                -
              </button>
              <span>{cartQuantity}</span>
              <button
                type="button"
                onClick={() => void handleQuantityChange(cartQuantity + 1)}
                aria-label={`Increase ${product.name} quantity`}
              >
                +
              </button>
            </div>
          ) : (
            product.availability === "out-of-stock" ? (
              <span className="product-card__stock-badge">Out of stock</span>
            ) : (
              <button
                type="button"
                className={`product-card__quick-add ${cartQuantity > 0 ? "is-added" : ""}`}
                onClick={() => void handleAddToCart()}
                aria-label={`Add ${product.name} to cart`}
              >
                <span>ADD</span>
                {cartQuantity > 0 ? <strong>{cartQuantity}</strong> : null}
              </button>
            )
          )}
        </div>
      </div>

      <div className="product-card__content">
        <span className="product-card__brand">{product.brand}</span>

        <Link className="product-card__title" to={`/products/${product.slug}`}>
          {product.name}
        </Link>

        <div className="product-card__pricing">
          <div>
            <span className="product-card__price-row">
              <strong>{formatCurrency(product.price)}</strong>
              <span className="product-card__original-price">{formatCurrency(product.originalPrice)}</span>
            </span>
            <em>{product.discountPercentage}% OFF</em>
          </div>
        </div>
        <p className="product-card__copy">{product.shortDescription}</p>
        <div className="product-card__footer">
          {cartQuantity > 0 ? (
            <div className="product-card__quantity-control" aria-label={`${product.name} cart quantity`}>
              <button
                type="button"
                onClick={() => void handleQuantityChange(cartQuantity - 1)}
                aria-label={`Reduce ${product.name} quantity`}
              >
                -
              </button>
              <span>{cartQuantity}</span>
              <button
                type="button"
                onClick={() => void handleQuantityChange(cartQuantity + 1)}
                aria-label={`Increase ${product.name} quantity`}
              >
                +
              </button>
            </div>
          ) : (
            <button
              type="button"
              disabled={product.availability === "out-of-stock"}
              onClick={() => void handleAddToCart()}
            >
              {product.availability === "out-of-stock" ? "Unavailable" : compact ? "ADD" : "Add to cart"}
            </button>
          )}
          <button
            className="product-card__share"
            type="button"
            onClick={() => void handleToggleWishlist()}
          >
            {savedToWishlist ? "In wishlist" : "Add to wishlist"}
          </button>
        </div>
      </div>
    </article>
  );
};

export default ProductCard;
